import { useEffect, useMemo, useState } from "react";
import { ErpShell } from "../components/layout/ErpShell.js";
import { FILTER_INPUT, FilterField, FilterPanel } from "../components/common/FilterBar.js";
import { useModals } from "../components/common/ModalsProvider.js";
import { useAuth } from "../hooks/useAuth.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { trpc } from "../lib/trpc.js";
import { useT } from "../i18n/index.js";
import { Paginacao } from "../components/common/Paginacao.js";

const PAGE_SIZE = 50;

type BankProvider = "societe_generale" | "wise" | "qonto" | "revolut" | "other";

interface AccountForm {
  label: string;
  provider: BankProvider;
  iban: string;
  bicSwift: string;
  bankName: string;
  currency: string;
  openingBalance: string;
  openingDate: string; // yyyy-mm-dd
  notes: string;
  reason: string;
  openingReason: string;
}

const emptyForm: AccountForm = {
  label: "",
  provider: "societe_generale",
  iban: "",
  bicSwift: "",
  bankName: "",
  currency: "EUR",
  openingBalance: "0",
  openingDate: new Date().toISOString().slice(0, 10),
  notes: "",
  reason: "",
  openingReason: "",
};

export default function BankAccounts() {
  const { user } = useAuth();
  const canEdit = useCanEdit();
  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );

  const [companyId, setCompanyId] = useSelectedCompany(accessibleCompanies);
  const [q, setQ] = useState("");
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">("active");
  const [offset, setOffset] = useState(0);
  // CEO 2026-10-18 — "Ligar ao Wise" pela tela (token + perfil + chave SCA).
  const [wiseModal, setWiseModal] = useState<number | null>(null);

  const filters = useMemo(
    () => ({
      companyId: companyId ?? 0,
      q: q || undefined,
      active: activeFilter === "all" ? undefined : activeFilter === "active",
      limit: PAGE_SIZE,
      offset,
    }),
    [companyId, q, activeFilter, offset],
  );

  const listQuery = trpc.bankAccounts.list.useQuery(filters, {
    enabled: companyId !== null,
  });
  const utils = trpc.useUtils();
  // CEO 2026-10-31 — "não sei como fazer": a conta padrão também direto na lista.
  const padraoMut = trpc.bankAccounts.setDefaultForInvoices.useMutation({
    onSuccess: () => {
      void utils.bankAccounts.invalidate();
      void utils.customerInvoices.bankAccountOptions.invalidate();
    },
  });

  const [modal, setModal] = useState<
    { mode: "create" } | { mode: "edit"; bankAccountId: number } | null
  >(null);
  const t = useT();

  if (!user) return null;

  // Apenas admin (ou super) pode criar conta. Mas qualquer member da empresa vê a lista.
  const isAdmin =
    user.isSuperAdmin ||
    user.memberships.some((m) => m.companyId === companyId && m.active && m.role === "admin");

  if (accessibleCompanies.length === 0) {
    return (
      <Shell>
        <p className="text-sm text-neutral-400">{t("bankAccounts.noCompanyAccess")}</p>
      </Shell>
    );
  }

  const data = listQuery.data;
  const total = data?.total ?? 0;

  return (
    <Shell>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <FilterField label={t("bankAccounts.companyLabel")}>
          <select
            value={companyId ?? ""}
            onChange={(e) => {
              setCompanyId(Number(e.target.value));
              setOffset(0);
            }}
            className={inputClass + " w-64"}
          >
            {accessibleCompanies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </FilterField>
        {canEdit && isAdmin && (
          <button
            onClick={() => setModal({ mode: "create" })}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime px-4 py-1.5 text-sm font-medium"
          >
            {t("bankAccounts.newBtn")}
          </button>
        )}
      </div>

      <FilterPanel testId="bankAccounts-filter-panel">
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <FilterField label={t("bankAccounts.searchLabel")} className="grow max-w-xs">
            <input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setOffset(0);
              }}
              placeholder={t("bankAccounts.searchPlaceholder")}
              className={FILTER_INPUT + " w-full"}
            />
          </FilterField>
          <FilterField label={t("bankAccounts.statusLabel")}>
            <select
              value={activeFilter}
              onChange={(e) => {
                setActiveFilter(e.target.value as typeof activeFilter);
                setOffset(0);
              }}
              className={FILTER_INPUT + " w-full sm:w-40"}
            >
              <option value="active">{t("bankAccounts.filterActive")}</option>
              <option value="inactive">{t("bankAccounts.filterInactive")}</option>
              <option value="all">{t("bankAccounts.filterAll")}</option>
            </select>
          </FilterField>
        </div>
      </FilterPanel>

      <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-hidden">
        {listQuery.isLoading && (
          <p className="p-4 text-sm text-neutral-500">{t("bankAccounts.loading")}</p>
        )}
        {listQuery.error && <p className="p-4 text-sm text-red-400">{listQuery.error.message}</p>}
        {data && data.rows.length === 0 && (
          <p className="p-4 text-sm text-neutral-500">{t("bankAccounts.emptyState")}</p>
        )}
        {data && data.rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-3 py-2">{t("bankAccounts.labelField")}</th>
                <th className="text-left px-3 py-2">{t("bankAccounts.bankLabel")}</th>
                <th className="text-left px-3 py-2 font-mono">IBAN</th>
                <th className="text-left px-3 py-2 w-16">{t("bankAccounts.colCurrency")}</th>
                <th className="text-right px-3 py-2 w-32">
                  {t("bankAccounts.openingBalanceLabel")}
                </th>
                <th className="text-left px-3 py-2 w-28">{t("bankAccounts.colOpening")}</th>
                <th className="text-left px-3 py-2 w-20">{t("bankAccounts.statusLabel")}</th>
                <th className="text-left px-3 py-2">{t("bankAccounts.colWise")}</th>
                <th className="px-3 py-2 w-20 text-right">{t("bankAccounts.colActions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {data.rows.map((a) => (
                <tr key={a.id} className={a.active ? "" : "opacity-60"}>
                  <td className="px-3 py-2">
                    {a.label}
                    {/* F-conta-na-fatura (CEO 2026-10-31) — conta impressa nas faturas. */}
                    {a.defaultForInvoices && (
                      <span
                        className="ml-2 rounded bg-lime/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-lime"
                        data-testid={`ba-default-invoices-${a.id}`}
                      >
                        {t("bankAccounts.defaultInvoicesBadge")}
                      </span>
                    )}
                    {a.bicSwift && (
                      <div className="text-xs text-neutral-600 font-mono">{a.bicSwift}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-neutral-400">{a.bankName ?? "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono text-neutral-400">
                    {a.iban ?? <span className="text-neutral-700">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">{a.currency}</td>
                  <td className="px-3 py-2 text-xs text-right font-mono">
                    {Number(a.openingBalance).toLocaleString("fr-FR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td className="px-3 py-2 text-xs text-neutral-400">{a.openingDate}</td>
                  <td className="px-3 py-2">
                    <ActiveBadge active={a.active} />
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {a.provider === "wise" && companyId !== null ? (
                      <WiseCell
                        companyId={companyId}
                        account={a}
                        canManage={canEdit && isAdmin}
                        onConfigure={() => setWiseModal(a.id)}
                      />
                    ) : (
                      <span className="text-neutral-700">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canEdit && isAdmin && (
                      <span className="inline-flex flex-wrap justify-end gap-3">
                        {a.active && !a.defaultForInvoices && companyId !== null && (
                          <button
                            onClick={() => padraoMut.mutate({ companyId, bankAccountId: a.id })}
                            disabled={padraoMut.isPending}
                            title={t("bankAccounts.makeDefaultInvoicesBtn")}
                            className="whitespace-nowrap text-xs text-lime hover:underline disabled:opacity-40"
                            data-testid={`ba-row-make-default-${a.id}`}
                          >
                            {t("bankAccounts.makeDefaultInvoicesShort")}
                          </button>
                        )}
                        <button
                          onClick={() => setModal({ mode: "edit", bankAccountId: a.id })}
                          className="text-xs text-lime hover:underline"
                        >
                          {t("bankAccounts.editBtn")}
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > 0 && (
        <Paginacao
          total={total}
          pageSize={PAGE_SIZE}
          offset={offset}
          onOffset={setOffset}
          noun={total === 1 ? t("bankAccounts.accountSingular") : t("bankAccounts.accountPlural")}
          testId="bankAccounts-pagination"
        />
      )}

      {wiseModal !== null && companyId !== null && (
        <WiseConnectModal
          companyId={companyId}
          bankAccountId={wiseModal}
          onClose={() => setWiseModal(null)}
        />
      )}

      {modal && companyId !== null && (
        <BankAccountModal
          companyId={companyId}
          mode={modal.mode}
          bankAccountId={modal.mode === "edit" ? modal.bankAccountId : undefined}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            utils.bankAccounts.invalidate();
          }}
        />
      )}
    </Shell>
  );
}

function BankAccountModal({
  companyId,
  mode,
  bankAccountId,
  onClose,
  onSaved,
}: {
  companyId: number;
  mode: "create" | "edit";
  bankAccountId?: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { user } = useAuth();
  const modals = useModals();
  const detailQuery = trpc.bankAccounts.getById.useQuery(
    { companyId, bankAccountId: bankAccountId ?? 0 },
    {
      enabled: mode === "edit" && !!bankAccountId,
      retry: false,
      staleTime: 0,
      refetchOnMount: "always",
    },
  );
  const [form, setForm] = useState<AccountForm>(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const t = useT();

  useEffect(() => {
    if (mode !== "edit") return;
    const d = detailQuery.data;
    if (!d) return;
    setForm({
      label: d.label,
      provider: d.provider,
      iban: d.iban ?? "",
      bicSwift: d.bicSwift ?? "",
      bankName: d.bankName ?? "",
      currency: d.currency,
      openingBalance: String(d.openingBalance),
      openingDate: d.openingDate,
      notes: d.notes ?? "",
      reason: "",
      openingReason: "",
    });
  }, [mode, detailQuery.data?.id, detailQuery.dataUpdatedAt]);

  const isSuperAdmin = user?.isSuperAdmin ?? false;

  const ibanChanged =
    mode === "edit" && detailQuery.data ? form.iban !== (detailQuery.data.iban ?? "") : false;

  const openingChanged =
    mode === "edit" && detailQuery.data
      ? form.openingBalance !== String(detailQuery.data.openingBalance) ||
        form.openingDate !== detailQuery.data.openingDate
      : false;

  const createMutation = trpc.bankAccounts.create.useMutation({
    onSuccess: onSaved,
    onError: (err) => setError(err.message),
  });
  const updateMutation = trpc.bankAccounts.update.useMutation({
    onSuccess: onSaved,
    onError: (err) => setError(err.message),
  });
  const updateOpeningMutation = trpc.bankAccounts.updateOpening.useMutation({
    onSuccess: onSaved,
    onError: (err) => setError(err.message),
  });
  const deactivateMutation = trpc.bankAccounts.deactivate.useMutation({
    onSuccess: onSaved,
    onError: (err) => setError(err.message),
  });
  const reactivateMutation = trpc.bankAccounts.reactivate.useMutation({
    onSuccess: onSaved,
    onError: (err) => setError(err.message),
  });
  // F-conta-na-fatura (CEO 2026-10-31) — conta padrão impressa nas faturas.
  const defaultInvMutation = trpc.bankAccounts.setDefaultForInvoices.useMutation({
    onSuccess: onSaved,
    onError: (err) => setError(err.message),
  });

  const pending =
    createMutation.isPending ||
    updateMutation.isPending ||
    updateOpeningMutation.isPending ||
    deactivateMutation.isPending ||
    reactivateMutation.isPending ||
    defaultInvMutation.isPending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const openingBalanceNum = Number(form.openingBalance);
    if (Number.isNaN(openingBalanceNum)) {
      setError(t("bankAccounts.invalidOpeningBalance"));
      return;
    }

    if (mode === "create") {
      createMutation.mutate({
        companyId,
        label: form.label.trim(),
        provider: form.provider,
        iban: form.iban.trim() || undefined,
        bicSwift: form.bicSwift.trim() || undefined,
        bankName: form.bankName.trim() || undefined,
        currency: form.currency.trim().toUpperCase(),
        notes: form.notes.trim() || undefined,
        openingBalance: openingBalanceNum,
        openingDate: new Date(form.openingDate),
        openingReason: form.openingReason.trim() || undefined,
      });
      return;
    }
    if (!bankAccountId) return;

    // Update parts: campos comuns (label/iban/...) → update normal; opening → updateOpening (super admin)
    const tasks: Promise<unknown>[] = [];
    if (
      form.label !== (detailQuery.data?.label ?? "") ||
      // CEO 2026-10-19: só o provedor mudado não disparava o update.
      form.provider !== detailQuery.data?.provider ||
      form.iban !== (detailQuery.data?.iban ?? "") ||
      form.bicSwift !== (detailQuery.data?.bicSwift ?? "") ||
      form.bankName !== (detailQuery.data?.bankName ?? "") ||
      form.currency !== detailQuery.data?.currency ||
      form.notes !== (detailQuery.data?.notes ?? "")
    ) {
      tasks.push(
        updateMutation.mutateAsync({
          companyId,
          bankAccountId,
          patch: {
            label: form.label.trim(),
            provider: form.provider,
            iban: form.iban.trim() || undefined,
            bicSwift: form.bicSwift.trim() || undefined,
            bankName: form.bankName.trim() || undefined,
            currency: form.currency.trim().toUpperCase(),
            notes: form.notes.trim() || undefined,
          },
          reason: form.reason.trim() || undefined,
        }),
      );
    }
    if (openingChanged) {
      if (!isSuperAdmin) {
        setError(t("bankAccounts.onlySuperAdminOpening"));
        return;
      }
      if (!form.openingReason.trim()) {
        setError(t("bankAccounts.openingReasonRequired"));
        return;
      }
      tasks.push(
        updateOpeningMutation.mutateAsync({
          companyId,
          bankAccountId,
          openingBalance: openingBalanceNum,
          openingDate: new Date(form.openingDate),
          reason: form.openingReason.trim(),
        }),
      );
    }
    if (tasks.length === 0) {
      onClose();
      return;
    }
    try {
      await Promise.all(tasks);
    } catch {
      // erro já tratado nos onError de cada mutation
    }
  }

  return (
    <ModalShell
      title={
        mode === "create" ? t("bankAccounts.modalTitleCreate") : t("bankAccounts.modalTitleEdit")
      }
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("bankAccounts.labelField")} required>
            <input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              required
              maxLength={200}
              disabled={pending}
              placeholder={t("bankAccounts.labelPlaceholder")}
              className={inputClass}
            />
          </Field>
          <Field label={t("bankAccounts.bankLabel")}>
            <input
              value={form.bankName}
              onChange={(e) => setForm({ ...form, bankName: e.target.value })}
              maxLength={200}
              disabled={pending}
              className={inputClass}
            />
          </Field>
          <Field label="IBAN">
            <input
              value={form.iban}
              onChange={(e) => setForm({ ...form, iban: e.target.value.toUpperCase() })}
              maxLength={34}
              disabled={pending}
              placeholder={t("bankAccounts.ibanPlaceholder")}
              className={`${inputClass} font-mono`}
            />
          </Field>
          <Field label="BIC / SWIFT">
            <input
              value={form.bicSwift}
              onChange={(e) => setForm({ ...form, bicSwift: e.target.value.toUpperCase() })}
              maxLength={11}
              disabled={pending}
              className={`${inputClass} font-mono`}
            />
          </Field>
          <Field label={t("bankAccounts.currencyIsoLabel")} required>
            <input
              value={form.currency}
              onChange={(e) =>
                setForm({ ...form, currency: e.target.value.toUpperCase().slice(0, 3) })
              }
              required
              maxLength={3}
              disabled={pending}
              className={inputClass}
            />
          </Field>
          <Field label={t("bankAccounts.providerLabel")} required>
            {/* CEO 2026-10-19: a conta Wise da BS nasceu como "other" e o campo
                ficava travado na edição (mostrando o valor cru "other"). O
                provedor pode mudar — o servidor já aceitava. */}
            <select
              value={form.provider}
              onChange={(e) => setForm({ ...form, provider: e.target.value as BankProvider })}
              disabled={pending}
              className={inputClass}
              data-testid="bank-account-provider"
            >
              <option value="societe_generale">{t("bankAccounts.providerSg")}</option>
              <option value="wise">{t("bankAccounts.providerWise")}</option>
              <option value="qonto">Qonto</option>
              <option value="revolut">Revolut</option>
              <option value="other">{t("bankAccounts.providerOther")}</option>
            </select>
          </Field>
          <Field label={t("bankAccounts.openingBalanceLabel")}>
            <input
              type="number"
              step="0.01"
              value={form.openingBalance}
              onChange={(e) => setForm({ ...form, openingBalance: e.target.value })}
              disabled={pending || (mode === "edit" && !isSuperAdmin)}
              className={`${inputClass} font-mono text-right`}
            />
            {mode === "edit" && !isSuperAdmin && (
              <p className="text-xs text-neutral-600 mt-1">
                {t("bankAccounts.onlySuperAdminEditsOpening")}
              </p>
            )}
            {mode === "create" && !isSuperAdmin && (
              <p className="text-xs text-neutral-600 mt-1">
                {t("bankAccounts.adminCreatesZeroBalance")}
              </p>
            )}
          </Field>
          <Field label={t("bankAccounts.openingDateLabel")} required>
            <input
              type="date"
              value={form.openingDate}
              onChange={(e) => setForm({ ...form, openingDate: e.target.value })}
              required
              disabled={pending || (mode === "edit" && !isSuperAdmin)}
              className={inputClass}
            />
          </Field>
        </div>

        <Field label={t("bankAccounts.notesLabel")}>
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            maxLength={2000}
            rows={2}
            disabled={pending}
            className={`${inputClass} resize-y`}
          />
        </Field>

        {mode === "edit" && ibanChanged && (
          <Field label={t("bankAccounts.ibanChangeReasonLabel")} required>
            <input
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              required
              maxLength={500}
              disabled={pending}
              className={inputClass}
            />
          </Field>
        )}
        {mode === "edit" && openingChanged && (
          <Field label={t("bankAccounts.openingChangeReasonLabel")} required>
            <input
              value={form.openingReason}
              onChange={(e) => setForm({ ...form, openingReason: e.target.value })}
              required
              maxLength={500}
              disabled={pending}
              className={inputClass}
            />
          </Field>
        )}
        {mode === "create" && Number(form.openingBalance) !== 0 && (
          <Field label={t("bankAccounts.openingNonZeroReasonLabel")} required>
            <input
              value={form.openingReason}
              onChange={(e) => setForm({ ...form, openingReason: e.target.value })}
              required
              maxLength={500}
              disabled={pending}
              className={inputClass}
            />
          </Field>
        )}

        {error && (
          <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-neutral-800">
          <div className="flex gap-2">
            {mode === "edit" && bankAccountId && detailQuery.data && (
              <>
                {detailQuery.data.active ? (
                  <button
                    type="button"
                    onClick={async () => {
                      const reason = await modals.promptReason({
                        title: `${t("bankAccounts.deactivateAccountPre")} "${detailQuery.data?.label ?? ""}"`,
                        message: t("bankAccounts.deactivateMessage"),
                        placeholder: t("bankAccounts.deactivateReasonPlaceholder"),
                        confirmLabel: t("bankAccounts.deactivateBtn"),
                        tone: "danger",
                        required: true,
                      });
                      if (!reason) return;
                      deactivateMutation.mutate({ companyId, bankAccountId, reason });
                    }}
                    disabled={pending}
                    className="rounded-md border border-red-500/40 hover:border-red-500 text-red-400 px-3 py-1.5 text-xs"
                  >
                    {t("bankAccounts.deactivateBtn")}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => reactivateMutation.mutate({ companyId, bankAccountId })}
                    disabled={pending}
                    className="rounded-md border border-lime/40 hover:border-lime text-lime px-3 py-1.5 text-xs"
                  >
                    {t("bankAccounts.reactivateBtn")}
                  </button>
                )}
                {detailQuery.data.active && !detailQuery.data.defaultForInvoices && (
                  <button
                    type="button"
                    onClick={() => defaultInvMutation.mutate({ companyId, bankAccountId })}
                    disabled={pending}
                    className="rounded-md border border-lime/40 hover:border-lime text-lime px-3 py-1.5 text-xs"
                    data-testid="ba-make-default-invoices"
                  >
                    {t("bankAccounts.makeDefaultInvoicesBtn")}
                  </button>
                )}
              </>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={pending}
              className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
            >
              {t("bankAccounts.cancelBtn")}
            </button>
            <button
              type="submit"
              disabled={pending}
              className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
            >
              {pending
                ? t("bankAccounts.saving")
                : mode === "create"
                  ? t("bankAccounts.createBtn")
                  : t("bankAccounts.saveBtn")}
            </button>
          </div>
        </div>
      </form>
    </ModalShell>
  );
}

const inputClass =
  "w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none disabled:opacity-50";

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs text-neutral-400 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children}
    </div>
  );
}

function ActiveBadge({ active }: { active: boolean }) {
  const t = useT();
  return active ? (
    <span className="inline-block rounded bg-lime/20 text-lime px-2 py-0.5 text-xs">
      {t("bankAccounts.badgeActive")}
    </span>
  ) : (
    <span className="inline-block rounded bg-neutral-700/40 text-neutral-400 px-2 py-0.5 text-xs">
      {t("bankAccounts.badgeInactive")}
    </span>
  );
}

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-2xl bg-neutral-950 border border-neutral-800 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-neutral-800 flex items-center justify-between sticky top-0 bg-neutral-950">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-300 text-lg leading-none"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <ErpShell>
      <div className="space-y-4">{children}</div>
    </ErpShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CEO 2026-10-18 — Wise pela tela: estado da ligação na linha + assistente
// "Ligar ao Wise" (token → perfil → chave pública para cadastrar no Wise →
// teste). O servidor guarda tudo cifrado e nunca devolve o token.
// ─────────────────────────────────────────────────────────────────────────────

type WiseAccountRow = {
  id: number;
  label: string;
  currency: string;
  lastSyncedAt: string | Date | null;
  hasApiCredentials: boolean;
  wise: {
    profileId: string | number;
    balanceId: number | null;
    hasKey: boolean;
    publicKeyPem: string | null;
  } | null;
};

function WiseCell({
  companyId,
  account,
  canManage,
  onConfigure,
}: {
  companyId: number;
  account: WiseAccountRow;
  canManage: boolean;
  onConfigure: () => void;
}) {
  const t = useT();
  const modals = useModals();
  const utils = trpc.useUtils();
  const refresh = () => void utils.bankAccounts.invalidate();
  const testMut = trpc.bankAccounts.wiseTest.useMutation({
    onSuccess: (r) =>
      void modals.alert({
        title: t("bankAccounts.wiseTestBtn"),
        message: `${t("bankAccounts.wiseTestOkPre")} ${r.count} ${t("bankAccounts.wiseTestCountSuf")}${
          r.scaUsed ? ` · ${t("bankAccounts.wiseTestSca")}` : ""
        }${r.sample.length ? `\n\n${r.sample.join("\n")}` : ""}`,
      }),
    onError: (e) =>
      void modals.alert({
        title: t("bankAccounts.wiseTestBtn"),
        message: e.message,
        tone: "danger",
      }),
  });
  const syncMut = trpc.bankImport.syncWise.useMutation({
    onSuccess: (r) => {
      refresh();
      void modals.alert({
        title: t("bankAccounts.wiseSyncNowBtn"),
        message: `${t("bankAccounts.wiseSyncDonePre")} ${r.inserted} ${t("bankAccounts.wiseSyncNewSuf")} · ${r.skipped} ${t("bankAccounts.wiseSyncDupSuf")}`,
      });
    },
    onError: (e) =>
      void modals.alert({
        title: t("bankAccounts.wiseSyncNowBtn"),
        message: e.message,
        tone: "danger",
      }),
  });
  const clearMut = trpc.bankAccounts.clearApiCredentials.useMutation({
    onSuccess: refresh,
    onError: (e) =>
      void modals.alert({
        title: t("bankAccounts.wiseDisconnectBtn"),
        message: e.message,
        tone: "danger",
      }),
  });
  const busy = testMut.isPending || syncMut.isPending || clearMut.isPending;
  const ligado = account.hasApiCredentials && account.wise != null;
  const ultima = account.lastSyncedAt
    ? new Date(account.lastSyncedAt).toLocaleString("fr-FR", {
        dateStyle: "short",
        timeStyle: "short",
      })
    : null;
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-1.5">
        {ligado ? (
          <span className="badge b-ok" data-testid="wise-linked">
            {t("bankAccounts.wiseLinked")}
          </span>
        ) : (
          <span className="badge b-warn" data-testid="wise-not-linked">
            {t("bankAccounts.wiseNotLinked")}
          </span>
        )}
        {ligado && account.wise && !account.wise.hasKey && (
          <span className="badge b-warn">{t("bankAccounts.wiseNoKey")}</span>
        )}
        {ligado && (
          <span className="text-neutral-500">
            {ultima
              ? `${t("bankAccounts.wiseLastSyncPre")} ${ultima}`
              : t("bankAccounts.wiseNeverSynced")}
          </span>
        )}
      </div>
      {canManage && (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={onConfigure} className="text-lime hover:underline">
            {ligado ? t("bankAccounts.wiseReconfigureBtn") : t("bankAccounts.wiseConnectBtn")}
          </button>
          {ligado && (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => testMut.mutate({ companyId, bankAccountId: account.id })}
                className="text-neutral-300 hover:underline disabled:opacity-40"
              >
                {testMut.isPending ? "…" : t("bankAccounts.wiseTestBtn")}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => syncMut.mutate({ companyId, bankAccountId: account.id })}
                className="text-neutral-300 hover:underline disabled:opacity-40"
              >
                {syncMut.isPending ? "…" : t("bankAccounts.wiseSyncNowBtn")}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={async () => {
                  const reason = await modals.promptReason({
                    title: `${t("bankAccounts.wiseDisconnectTitle")} — ${account.label}`,
                    message: t("bankAccounts.wiseDisconnectMsg"),
                    confirmLabel: t("bankAccounts.wiseDisconnectBtn"),
                    tone: "danger",
                    required: true,
                  });
                  if (!reason) return;
                  clearMut.mutate({ companyId, bankAccountId: account.id, reason });
                }}
                className="text-red-400 hover:underline disabled:opacity-40"
              >
                {t("bankAccounts.wiseDisconnectBtn")}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function WiseConnectModal({
  companyId,
  bankAccountId,
  onClose,
}: {
  companyId: number;
  bankAccountId: number;
  onClose: () => void;
}) {
  const t = useT();
  const utils = trpc.useUtils();
  const detailQuery = trpc.bankAccounts.getById.useQuery({ companyId, bankAccountId });
  const account = detailQuery.data;
  const [token, setToken] = useState("");
  const [profiles, setProfiles] = useState<Array<{
    id: number;
    type: string;
    name: string;
  }> | null>(null);
  const [profileId, setProfileId] = useState<number | null>(null);
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [linkedInfo, setLinkedInfo] = useState<string | null>(null);
  const [keyReused, setKeyReused] = useState(false);
  const [copied, setCopied] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reconfiguração: a chave pública já existe — mostra direto a etapa 3.
  useEffect(() => {
    if (account?.wise?.publicKeyPem && publicKey == null && profiles == null) {
      setPublicKey(account.wise.publicKeyPem);
    }
  }, [account, publicKey, profiles]);

  const profilesMut = trpc.bankAccounts.wiseProfiles.useMutation({
    onSuccess: (r) => {
      setError(null);
      setProfiles(r.profiles);
      const business = r.profiles.find((p) => p.type.toLowerCase() === "business");
      setProfileId(business?.id ?? r.profiles[0]?.id ?? null);
    },
    onError: (e) => setError(e.message),
  });
  const connectMut = trpc.bankAccounts.wiseConnect.useMutation({
    onSuccess: (r) => {
      setError(null);
      setPublicKey(r.publicKeyPem);
      setKeyReused(r.keyReused);
      setLinkedInfo(
        `${t("bankAccounts.wiseLinkedTo")} "${r.profileName}" · ${t("bankAccounts.wiseBalancePre")} ${r.balance.amount.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} ${r.balance.currency}`,
      );
      void utils.bankAccounts.invalidate();
    },
    onError: (e) => setError(e.message),
  });
  const testMut = trpc.bankAccounts.wiseTest.useMutation({
    onSuccess: (r) => {
      setError(null);
      setTestResult(
        `${t("bankAccounts.wiseTestOkPre")} ${r.count} ${t("bankAccounts.wiseTestCountSuf")}${
          r.scaUsed ? ` · ${t("bankAccounts.wiseTestSca")}` : ""
        }`,
      );
    },
    onError: (e) => {
      setTestResult(null);
      setError(e.message);
    },
  });
  const pending = profilesMut.isPending || connectMut.isPending || testMut.isPending;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-neutral-950 border border-neutral-800 rounded-lg shadow-xl p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">{t("bankAccounts.wiseModalTitle")}</h2>
            <p className="text-xs text-neutral-500">{account?.label ?? ""}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-300 text-lg leading-none"
          >
            ×
          </button>
        </div>

        {/* Etapa 1 — token */}
        {publicKey == null && (
          <section className="space-y-2 rounded-md border border-neutral-800 p-3">
            <h3 className="text-sm font-semibold">{t("bankAccounts.wiseStep1")}</h3>
            <p className="text-xs text-neutral-400 whitespace-pre-line">
              {t("bankAccounts.wiseTokenHint")}
            </p>
            <label className="block text-xs uppercase tracking-wider text-neutral-500">
              {t("bankAccounts.wiseTokenLabel")}
            </label>
            <div className="flex gap-2">
              <input
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                autoComplete="off"
                className={inputClass + " flex-1 font-mono"}
                data-testid="wise-token"
              />
              <button
                type="button"
                disabled={pending || token.trim().length < 10}
                onClick={() =>
                  profilesMut.mutate({ companyId, bankAccountId, apiToken: token.trim() })
                }
                className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-40 px-3 py-1.5 text-sm font-medium whitespace-nowrap"
                data-testid="wise-fetch-profiles"
              >
                {profilesMut.isPending ? "…" : t("bankAccounts.wiseFetchProfilesBtn")}
              </button>
            </div>
          </section>
        )}

        {/* Etapa 2 — perfil */}
        {publicKey == null && profiles && (
          <section className="space-y-2 rounded-md border border-neutral-800 p-3">
            <h3 className="text-sm font-semibold">{t("bankAccounts.wiseStep2")}</h3>
            {profiles.length === 0 && (
              <p className="text-xs text-amber-300">{t("bankAccounts.wiseNoProfiles")}</p>
            )}
            {profiles.map((p) => (
              <label key={p.id} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="wise-profile"
                  checked={profileId === p.id}
                  onChange={() => setProfileId(p.id)}
                />
                <span>
                  {p.name} <span className="text-xs text-neutral-500">({p.type})</span>
                </span>
              </label>
            ))}
            <button
              type="button"
              disabled={pending || profileId == null}
              onClick={() =>
                profileId != null &&
                connectMut.mutate({ companyId, bankAccountId, apiToken: token.trim(), profileId })
              }
              className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-40 px-3 py-1.5 text-sm font-medium"
              data-testid="wise-link"
            >
              {connectMut.isPending ? "…" : t("bankAccounts.wiseLinkBtn")}
            </button>
          </section>
        )}

        {/* Etapa 3 — chave pública + teste */}
        {publicKey != null && (
          <section className="space-y-2 rounded-md border border-lime/40 bg-lime/5 p-3">
            {linkedInfo && <p className="text-sm text-lime">✓ {linkedInfo}</p>}
            {keyReused && (
              <p className="text-xs text-neutral-400">{t("bankAccounts.wiseKeyReused")}</p>
            )}
            <h3 className="text-sm font-semibold">{t("bankAccounts.wiseStep3")}</h3>
            <p className="text-xs text-neutral-300 whitespace-pre-line">
              {t("bankAccounts.wiseKeyHint")}
            </p>
            <textarea
              readOnly
              value={publicKey}
              rows={7}
              className={inputClass + " w-full font-mono text-[11px]"}
              onFocus={(e) => e.currentTarget.select()}
              data-testid="wise-public-key"
            />
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(publicKey);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2500);
                  } catch {
                    /* sem clipboard: o usuário seleciona e copia */
                  }
                }}
                className="rounded-md border border-neutral-700 hover:border-neutral-500 px-3 py-1.5 text-sm"
              >
                {copied ? `✓ ${t("bankAccounts.wiseCopied")}` : t("bankAccounts.wiseCopyBtn")}
              </button>
              {/* CEO 2026-10-19: o Wise pede a chave como ARQUIVO (.pem), não colada. */}
              <button
                type="button"
                onClick={() => {
                  const blob = new Blob([publicKey.endsWith("\n") ? publicKey : publicKey + "\n"], {
                    type: "application/x-pem-file",
                  });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "chave-gestion-wise.pem";
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                }}
                className="rounded-md border border-neutral-700 hover:border-neutral-500 px-3 py-1.5 text-sm"
                data-testid="wise-download-key"
              >
                {t("bankAccounts.wiseDownloadKeyBtn")}
              </button>
              <button
                type="button"
                disabled={pending}
                onClick={() => testMut.mutate({ companyId, bankAccountId })}
                className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-40 px-3 py-1.5 text-sm font-medium"
                data-testid="wise-test"
              >
                {testMut.isPending ? "…" : t("bankAccounts.wiseTestBtn")}
              </button>
              {account?.wise?.publicKeyPem && (
                <button
                  type="button"
                  onClick={() => {
                    setPublicKey(null);
                    setProfiles(null);
                    setTestResult(null);
                  }}
                  className="text-xs text-neutral-400 hover:underline"
                >
                  {t("bankAccounts.wiseNewTokenBtn")}
                </button>
              )}
            </div>
            {testResult && (
              <p className="text-sm text-lime" data-testid="wise-test-ok">
                ✓ {testResult}
              </p>
            )}
            <p className="text-xs text-neutral-500">{t("bankAccounts.wiseAutoNote")}</p>
          </section>
        )}

        {error && (
          <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2 whitespace-pre-wrap">
            {error}
          </div>
        )}

        <div className="flex justify-end pt-2 border-t border-neutral-800">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
          >
            {publicKey != null ? t("bankAccounts.wiseDoneBtn") : t("bankAccounts.cancelBtn")}
          </button>
        </div>
      </div>
    </div>
  );
}
